import { array, ord, string } from 'fp-ts';
import { useContext, useState } from 'react';
import { pipe } from 'fp-ts/function';
import { WithServiceId } from '@mews-commander/types';
import { ConfirmationDialog, OverlayedLoader } from '@mews-commander/ui';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Button, Card, Stack, StandaloneTable as Table, usePaginatedData, useTableState } from '@optimus-web/core';
import { RestrictionType } from '@mews-commander/data-access';
import { getServiceTimeUnit, isDayUseService } from '@mews-commander/legacy-entities/src/ducks/services';
import { useCommanderSelector } from '@mews-commander/utils';
import { FormType, FormValues, PreviewData, RestrictionPreviewRowData } from '../types';
import { VisitHasIntegrationRestrictionsContext } from '../../contexts';
import { createColumnMapping, originColumn } from './mapping';
import { PreviewBanner } from './PreviewBanner';

interface RestrictionSplicingPreviewProps extends WithServiceId {
    formType: FormType;
    formValues?: FormValues;
    previewData: PreviewData;
    handleGoBack: () => void;
    handleSaveChanges: () => Promise<void>;
}
const submitButtonTextKeyMap = {
    createForm: TextKey.CreateRestrictions,
    editForm: TextKey.EditRestriction,
    deleteForm: TextKey.DeleteRestrictions,
};

const byStartDate = pipe(
    string.Ord,
    ord.contramap(
        (restriction: RestrictionPreviewRowData<'create'> | RestrictionPreviewRowData<'delete'>) =>
            restriction.conditions.startUtc ?? ''
    )
);

const byAction = pipe(
    string.Ord,
    ord.contramap(
        (restriction: RestrictionPreviewRowData<'create'> | RestrictionPreviewRowData<'delete'>) => restriction.action
    ),
    ord.reverse
);

export const RestrictionSplicingPreview = ({
    formType,
    formValues,
    serviceId,
    previewData,
    handleGoBack,
    handleSaveChanges: saveChanges,
}: RestrictionSplicingPreviewProps) => {
    const [tableState, setTableState] = useTableState<RestrictionPreviewRowData>();

    const [isDialogOpen, toggleDialog] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const timeUnit = useCommanderSelector((state) => getServiceTimeUnit(state, { id: serviceId }));
    const isServiceDayUse = useCommanderSelector((state) => isDayUseService(state, { id: serviceId }));
    const visitHasIntegrationRestrictions = useContext(VisitHasIntegrationRestrictionsContext);
    const baseColumnMapping = createColumnMapping({ timeUnit, isServiceDayUse, serviceId });

    const data = pipe(
        [...previewData.delete, ...previewData.create],
        array.sortBy([byStartDate, byAction]),
        (sortedArray) => [...previewData.keep, ...sortedArray]
    );

    const containsInvalidRestrictions = array.isNonEmpty(previewData.invalid);
    const isCreatingRestrictions = array.isNonEmpty(previewData.create);
    const isDeletingRestrictions = array.isNonEmpty(previewData.delete);
    const isKeepingRestrictions = array.isNonEmpty(previewData.keep);
    const isOnlyKeepingRestrictions = isKeepingRestrictions && !isCreatingRestrictions && !isDeletingRestrictions;

    const handleSaveChanges = async () => {
        if (formType === 'deleteForm') {
            toggleDialog(true);
        } else {
            setIsSubmitting(true);
            await saveChanges();
            setIsSubmitting(false);
        }
    };

    const { data: paginatedData, totalRowCount } = usePaginatedData(data, tableState.pagination);

    const isDeletingEmptyExceptionRestrictions = pipe(
        previewData.delete,
        array.filter((restriction) => {
            const areExceptionsEmpty = Object.values(restriction.exceptions).every((value) => value === null);
            return areExceptionsEmpty && restriction.conditions.type === RestrictionType.ClosedToStay;
        }),
        array.isNonEmpty
    );

    const isKeepingEmptyExceptionRestrictions = previewData.keep.some((restriction) =>
        Object.values(restriction.exceptions).every((value) => value === null)
    );

    const previewBannerCommonProps = {
        containsInvalidRestrictions,
        isCreatingRestrictions,
        isDeletingRestrictions,
        isDeletingEmptyExceptionRestrictions,
    };

    const areExceptionFormValuesBlank =
        formValues && formValues.formType === 'createForm'
            ? formValues?.minAdvance === undefined &&
              formValues?.maxAdvance === undefined &&
              formValues?.minLength === undefined &&
              formValues?.maxLength === undefined &&
              formValues?.minPrice === undefined &&
              formValues?.maxPrice === undefined &&
              formValues?.maxReservationCount === undefined
            : false;

    return (
        <Stack spacing="200" vertical>
            <Card padding="200">
                <Stack spacing="200" vertical>
                    {formType === 'createForm' ? (
                        <PreviewBanner
                            areExceptionFormValuesBlank={areExceptionFormValuesBlank}
                            formType={formType}
                            isKeepingRestrictions={isKeepingRestrictions}
                            isKeepingEmptyExceptionRestrictions={isKeepingEmptyExceptionRestrictions}
                            {...previewBannerCommonProps}
                        />
                    ) : (
                        <PreviewBanner formType={formType} {...previewBannerCommonProps} />
                    )}
                    <Table
                        columns={
                            visitHasIntegrationRestrictions ? [...baseColumnMapping, originColumn] : baseColumnMapping
                        }
                        data={paginatedData}
                        uniqueRowId="id"
                        state={tableState}
                        onStateChange={setTableState}
                        amountOfRows={totalRowCount}
                        emptyState={{
                            primaryText: <LocalizedText textKey={TextKey.NoMatchingRestrictions} />,
                        }}
                    />
                </Stack>
            </Card>
            <Stack alignment="end" spacing="200">
                <Button onClick={handleGoBack} type="submit">
                    <LocalizedText textKey={TextKey.GoBack} />
                </Button>
                <Button
                    onClick={handleSaveChanges}
                    variant="primary"
                    disabled={array.isEmpty(data) || isOnlyKeepingRestrictions || containsInvalidRestrictions}
                    data-test-id={`${formType}-preview-table-button`}
                    type="submit"
                >
                    <LocalizedText textKey={submitButtonTextKeyMap[formType]} />
                </Button>
            </Stack>
            <ConfirmationDialog
                textKey={TextKey.DeleteRestrictionsQuestion}
                content={<LocalizedText textKey={TextKey.DeleteRestrictionsConfirmation} />}
                confirmButtonTextKey={TextKey.DeleteRestrictions}
                confirmButtonVariant="destructive"
                confirmChange={async () => {
                    setIsSubmitting(true);
                    await saveChanges();
                    setIsSubmitting(false);
                    toggleDialog(false);
                }}
                cancelChange={() => toggleDialog(false)}
                confirmationOpened={isDialogOpen}
                loading={isSubmitting}
            />
            <OverlayedLoader show={formType !== 'deleteForm' && isSubmitting} fixed />
        </Stack>
    );
};
